import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Copy } from "lucide-react";
import { toast } from "@/hooks/useToast";
import AvatarUploader from "@/components/shared/AvatarUploader";
import { Button } from "@/components/primitives/button";
import { Input } from "@primitives/input";
import { useAvatarUploader } from "@/middleware/hooks/useAvatarUploader";
import useUserAvatar from "@/middleware/hooks/useUserAvatar";
import { useAuthState } from "@/zustand/AuthState";
import { processAvatarBucketUrl } from "@/lib/utils";
import { copyTextToClipboard } from "@/lib/clipboard";
import { getEmail } from "@/server/services/emailService";
import { QUERY_KEYS } from "@/config/queryKeys";

/**
 * The Profile settings body (flag `settingsMiddleViews`;
 * docs/settings-middle-views/design.md). v1 shows only data the client
 * already has — avatar (uploader moved here from the left-column header),
 * username, and email via the existing `GET /umbra/auth/email` (the same call
 * the change-email wizard uses; email is NOT part of the login response or any
 * local store). The email query is session-cached (staleTime/gcTime Infinity,
 * key scoped by username) — reopening the view doesn't refetch, a successful
 * email change invalidates it (EmailChangeWizard), and a fetch error renders
 * as an error, never as "no email on file".
 *
 * Future profile fields (bio — placeholder row below, tag name, and further
 * board-decided attributes) become editable once the backend adds the storage.
 */

/**
 * Copy feedback follows the house pattern (message copy, guest-invite link):
 * a short toast — the icon never changes.
 */
function CopyButton({ value, label }: { value: string; label: string }) {
  const { t } = useTranslation();

  const handleCopy = async () => {
    const ok = await copyTextToClipboard(value);
    toast(
      ok
        ? { duration: 1000, description: t("copied"), variant: "default" }
        : {
            duration: 1000,
            description: t("couldNotCopyText"),
            variant: "destructive",
          },
    );
  };

  return (
    <Button
      size="icon"
      variant="ghost"
      aria-label={label}
      onClick={handleCopy}
      className="size-8 shrink-0 text-muted-foreground"
    >
      <Copy size={16} aria-hidden />
    </Button>
  );
}

function InfoRow({
  label,
  value,
  copyValue,
  copyLabel,
}: {
  label: string;
  value: ReactNode;
  /** When set, a copy-to-clipboard button renders on the row's right side. */
  copyValue?: string;
  copyLabel?: string;
}) {
  return (
    <div className="flex items-center justify-between gap-2 px-1">
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="text-xs text-muted-foreground">{label}</span>
        <span className="truncate text-sm">{value}</span>
      </div>
      {copyValue && copyLabel && (
        <CopyButton value={copyValue} label={copyLabel} />
      )}
    </div>
  );
}

export default function ProfileContent() {
  const { t } = useTranslation();
  const username = useAuthState((state) => state.username);
  const avatarAttachmentId = useUserAvatar(username);
  const avatarUrl = avatarAttachmentId
    ? processAvatarBucketUrl(avatarAttachmentId)
    : "";
  const { isUploading, blobUrl, handleFileChange, deleteAvatar } =
    useAvatarUploader({
      updateUserAvatarCallback: true,
    });

  // Key is scoped by username: the forced-reset path (401 refresh failure,
  // apiService.ts) resets auth WITHOUT queryClient.clear(), and logout is
  // SPA-only — an unscoped infinite-lifetime entry could show the previous
  // account's email to the next login.
  const { data, isPending, isError } = useQuery({
    queryKey: [...QUERY_KEYS.authEmail, username],
    queryFn: getEmail,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 1,
  });
  const email = data?.email ?? null;

  return (
    <>
      <div className="flex flex-col items-center py-2">
        <AvatarUploader
          handleFileChange={handleFileChange}
          handleDeleteAvatar={deleteAvatar}
          isUploading={isUploading}
          blobUrl={blobUrl}
          name={username}
          defaultAvatarUrl={avatarUrl}
        />
      </div>
      <InfoRow
        label={t("profile.username")}
        value={`@${username}`}
        copyValue={username}
        copyLabel={t("copy")}
      />
      <span className="h-px bg-foreground/10"></span>
      <InfoRow
        label={t("profile.email")}
        value={
          isPending ? (
            <span
              data-testid="email-skeleton"
              className="rounded my-0.5 inline-block h-4 w-44 animate-pulse bg-muted"
            />
          ) : isError ? (
            // Same split as the change-email wizard's current-email step:
            // a fetch error is an error, never "no email on file".
            <span className="text-destructive">{t("errorOccurred")}</span>
          ) : (
            (email ?? t("changeEmail.noEmail"))
          )
        }
        copyValue={email ?? undefined}
        copyLabel={t("copy")}
      />
      <span className="h-px bg-foreground/10"></span>
      {/* Bio ships disabled until the backend profile fields land
          (docs/settings-middle-views/design.md — Profile category). */}
      <div className="flex flex-col gap-1 px-1">
        <span className="text-xs text-muted-foreground">
          {t("profile.bio")}
        </span>
        <Input disabled placeholder={t("profile.bioComingSoon")} />
      </div>
    </>
  );
}
